import { int } from '!/api'
import { model as router } from '!/router'
import { attach, createStore, sample } from 'effector'
import { getLandingRegionUrlFromAlias } from '~/features/landingRegion'
import { preferencesFeatureModel } from '~/features/preferences'
import { PATH } from '~/shared/constants'
import { getAppsFlyerPlatformByKey } from '~/shared/helpers'
import { logger } from '~/shared/logger'

// re-export API type
export type Features = int.features.Features
export type AnotherPlatformObj = int.features.AnotherPlatformObj

export type PlatformListItem = {
  /** Backend key (ios, android, fireTv, …) */
  platform: string
  link: string
  /** AppsFlyer platform identifier (ios, android, roku, tizen, webos, …) */
  afPlatform: string
  appId: string
}

// get features flags and data
export const getFx: typeof int.features.getFx = attach({
  effect: int.features.getFx,
})
getFx.fail.watch((_) => logger.error('failed to load features:', _))

// features store
export const $ = createStore<Features | null>(null)
  .on(getFx.doneData, (_, features) => features)
  .on(getFx.fail, () => {})

export const $appleEnabled = //
  $.map((features) => features?.socialAuth?.appleEnabled ?? null)

export const $facebookEnabled = //
  $.map((features) => features?.socialAuth?.facebookEnabled ?? null)

export const $googleEnabled = //
  $.map((features) => features?.socialAuth?.googleEnabled ?? null)

// QR code
export const $QRCodeLoginEnabled = //
  $.map((features) => features?.qrCodeLoginEnabled ?? null)

export const $liveFeatureEnabled = //
  $.map((features) => features?.liveModuleEnabled ?? null)

export const $catchupEnabled = //
  $.map((features) => features?.catchUpEnabled ?? null)

export const $epgSettings = //
  $.map(
    (features) => features?.epgSettings ?? { pastHours: 24, futureHours: 48 }
  )

export const $liveRewindEnabled = //
  $.map((features) => features?.liveRewindEnabled ?? null)

// clear token if QRCodeLogin is not enabled or request failed
sample({
  clock: getFx.doneData,
  source: {
    query: router.$query,
    pathname: router.$pathname,
    preference: preferencesFeatureModel.$preference,
  },
  filter: ({ query, pathname }, data) =>
    query.has('qrAuthToken') &&
    pathname === PATH.HOME &&
    !data?.qrCodeLoginEnabled,
  fn: ({ preference }) => {
    const landingRegion = preference?.alias
    return `${PATH.HOME}${landingRegion ? getLandingRegionUrlFromAlias(landingRegion) : ''}`
  },
  target: router.navigatePush,
})
sample({
  clock: getFx.fail,
  source: {
    query: router.$query,
    pathname: router.$pathname,
    preference: preferencesFeatureModel.$preference,
  },
  filter: ({ query, pathname }) =>
    query.has('qrAuthToken') && pathname === PATH.HOME,
  fn: ({ preference }) => {
    const landingRegion = preference?.alias
    return `${PATH.HOME}${landingRegion ? getLandingRegionUrlFromAlias(landingRegion) : ''}`
  },
  target: router.navigatePush,
})

export const $platforms = $.map((features): PlatformListItem[] => {
  const platformObjs = features?.anotherPlatformObjs
  if (!platformObjs?.length) return []

  return platformObjs
    .map(({ platform, appId, url }) => ({
      platform,
      link: url ?? '',
      afPlatform: getAppsFlyerPlatformByKey(platform) ?? '',
      appId: appId ?? '',
    }))
    .sort(({ link: aLink }, { link: bLink }) => {
      if (aLink === bLink) return 0
      return bLink ? 1 : -1
    })
})
